<script>
  import { ChevronDown, Search, Send, X } from 'lucide-svelte';
  import {
    conversations,
    messages,
    selectedMessageIds,
    selectionMode,
    sendMessage,
    conversationId
  } from '$lib/websocket-client';
  import { avatarContentUrl } from '$lib/avatar';
  import SharePreviewCard from './sharing/SharePreviewCard.svelte';
  import ShareTargetItem from './sharing/ShareTargetItem.svelte';

  export let onClose = () => {};
  export let variant = 'desktop';

  let selectedTargets = new Set();
  let note = '';
  let searchKeyword = '';
  let activeTab = 'recent';

  const tabs = [
    { id: 'recent', label: 'Gần đây' },
    { id: 'group', label: 'Nhóm trò chuyện' },
    { id: 'direct', label: 'Bạn bè' }
  ];
  const alphabetRail = ['⌕', 'A', 'Ă', 'B', 'C', 'D', 'Đ', 'E', 'F', 'G', 'H', 'I', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'X', 'Y', 'Z', '#'];

  $: selectedMessages = $messages.filter((message) => $selectedMessageIds.has(message.id));
  $: mobileLayout = variant === 'mobile';
  $: filteredConversations = ($conversations || [])
    .filter((item) => item.conversationId !== $conversationId)
    .filter((item) => {
      if (activeTab === 'group') return item.type === 'group';
      if (activeTab === 'direct') return item.type === 'direct';
      return true;
    })
    .filter((item) => {
      const keyword = searchKeyword.trim().toLowerCase();
      if (!keyword) return true;
      return `${item.title || ''} ${item.lastMessagePreview || ''}`.toLowerCase().includes(keyword);
    });
  $: selectedConversations = ($conversations || []).filter((item) => selectedTargets.has(item.conversationId));

  function getInitial(value = '') {
    return value.trim().charAt(0)?.toUpperCase() || '?';
  }

  function getAvatarUrl(conversation) {
    return avatarContentUrl(conversation?.avatar);
  }

  function toggleTarget(id) {
    if (!id) return;
    selectedTargets.has(id) ? selectedTargets.delete(id) : selectedTargets.add(id);
    selectedTargets = new Set(selectedTargets);
  }

  function resetSelection() {
    selectedTargets = new Set();
  }

  function cleanup() {
    selectedTargets = new Set();
    note = '';
    selectedMessageIds.set(new Set());
    selectionMode.set(false);
    onClose?.();
  }

  function getSharedMessagePayload(message) {
    const media = message.media || {};
    const meta = message.meta || {};

    return {
      id: message.id,
      from: message.from,
      message: message.message,
      type: message.type,
      mime: message.mime || media.mime || meta.mime || '',
      name: message.name || media.name || meta.name || '',
      size:
        message.size ||
        message.fileSize ||
        message.file_size ||
        media.size ||
        media.fileSize ||
        media.file_size ||
        meta.size ||
        meta.fileSize ||
        meta.file_size ||
        '',
      created_at: message.created_at
    };
  }

  function submit() {
    if (!selectedTargets.size || !selectedMessages.length) return;

    sendMessage({
      type: 'message_share',
      fromConversationId: $conversationId,
      targetConversationIds: Array.from(selectedTargets),
      messages: selectedMessages.map(getSharedMessagePayload),
      note: note?.trim() || null
    });

    cleanup();
  }
</script>

<section class:mobile-layout={mobileLayout} class="share-sheet">
  <header class="sheet-header">
    {#if mobileLayout}
      <button class="close-button mobile-close" type="button" aria-label="Đóng" onclick={cleanup}>
        <X size={24} />
      </button>
      <div class="mobile-title">
        <h2>Chia sẻ</h2>
        <span>Đã chọn: {selectedTargets.size}</span>
      </div>
    {:else}
      <div>
        <h2>Chia sẻ</h2>
      </div>
      <button class="close-button" type="button" aria-label="Đóng" onclick={cleanup}>
        <X size={18} />
      </button>
    {/if}
  </header>

  <div class="toolbar">
    <label class="search-box">
      <Search size={16} />
      <input bind:value={searchKeyword} placeholder={mobileLayout ? 'Tìm kiếm' : 'Tìm kiếm...'} />
    </label>

    {#if !mobileLayout}
      <div class="tabs" role="tablist" aria-label="Bộ lọc cuộc trò chuyện">
        {#each tabs as tab (tab.id)}
          <button
            class:active={activeTab === tab.id}
            class="tab-button"
            type="button"
            onclick={() => (activeTab = tab.id)}>
            {tab.label}
          </button>
        {/each}
      </div>
    {/if}

    {#if !mobileLayout}
      <button class="filter-button" type="button">
        <span>Phân loại</span>
        <ChevronDown size={15} />
      </button>
    {/if}
  </div>

  <div class="sheet-body">
    <div class="recipient-panel">
      <div class="panel-title">
        <strong>{mobileLayout ? 'Trò chuyện gần đây' : 'Danh sách nhận'}</strong>
        {#if !mobileLayout}
          <span>{filteredConversations.length} cuộc trò chuyện</span>
        {/if}
      </div>

      <div class="recipient-list">
        {#each filteredConversations as item (item.conversationId)}
          <ShareTargetItem conversation={item} selected={selectedTargets.has(item.conversationId)} onToggle={toggleTarget} />
        {:else}
          <div class="empty-results">Không tìm thấy cuộc trò chuyện phù hợp.</div>
        {/each}
      </div>

      {#if mobileLayout}
        <div class="alphabet-rail" aria-hidden="true">
          {#each alphabetRail as letter}
            <span>{letter}</span>
          {/each}
        </div>
      {/if}
    </div>

    <aside class="summary-panel">
      {#if mobileLayout}
        <div class="selected-avatar-strip" class:empty={!selectedConversations.length}>
          {#if selectedConversations.length}
            {#each selectedConversations as item (item.conversationId)}
              {@const selectedAvatarUrl = getAvatarUrl(item)}
              <button class="selected-avatar" type="button" aria-label={`Bỏ chọn ${item.title || 'cuộc trò chuyện'}`} onclick={() => toggleTarget(item.conversationId)}>
                {#if selectedAvatarUrl}
                  <img src={selectedAvatarUrl} alt={item.title || 'Cuộc trò chuyện'} />
                {:else}
                  <span>{item.avatar || getInitial(item.title || 'Cuộc trò chuyện')}</span>
                {/if}
                <i><X size={13} /></i>
              </button>
            {/each}
          {:else}
            <span class="selected-avatar-empty">Chọn nơi nhận</span>
          {/if}
        </div>
      {:else}
        <div class="selection-header">
          <strong>Đã chọn: {selectedTargets.size}/100</strong>
          {#if selectedTargets.size}
            <button type="button" onclick={resetSelection}>Xóa</button>
          {/if}
        </div>
      {/if}

      {#if selectedConversations.length && !mobileLayout}
        <div class="selection-list">
          {#each selectedConversations as item (item.conversationId)}
            {@const selectedAvatarUrl = getAvatarUrl(item)}
            <button class="selection-chip" type="button" onclick={() => toggleTarget(item.conversationId)}>
              <span class="selection-chip-avatar">
                {#if selectedAvatarUrl}
                  <img src={selectedAvatarUrl} alt="" />
                {:else}
                  {item.avatar || getInitial(item.title || 'Cuộc trò chuyện')}
                {/if}
              </span>
              <span>{item.title || 'Cuộc trò chuyện'}</span>
              <X size={14} />
            </button>
          {/each}
        </div>
      {:else if !mobileLayout}
        <div class="selection-empty">Chọn ít nhất một nơi nhận để bật nút chia sẻ.</div>
      {/if}

      {#if mobileLayout}
        <SharePreviewCard messages={selectedMessages} compact />

        <label class="note-box">
          <span>Lời nhắn kèm theo</span>
          <textarea bind:value={note} rows="4" placeholder="Viết thêm một dòng giới thiệu hoặc ghi chú ngắn..."></textarea>
        </label>
      {/if}
    </aside>
  </div>

  {#if !mobileLayout}
    <div class="desktop-compose-panel">
      <SharePreviewCard messages={selectedMessages} compact />

      <label class="note-box">
        <textarea bind:value={note} rows="2" placeholder="Nhập tin nhắn..." aria-label="Lời nhắn kèm theo"></textarea>
      </label>
    </div>
  {/if}

  <footer class="sheet-footer">
    <button class="ghost-button" type="button" onclick={cleanup}>Hủy</button>
    <button class="send-button" type="button" disabled={!selectedTargets.size || !selectedMessages.length} onclick={submit}>
      <Send size={16} />
      <span>Chia sẻ ngay</span>
    </button>
  </footer>
</section>

<style>
  .share-sheet {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    overflow: hidden;
    box-sizing: border-box;
    border: 1px solid #d7dee8;
    border-radius: 0.32rem;
    background: #ffffff;
    box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
  }

  .sheet-header,
  .sheet-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    min-height: 3.6rem;
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid #e2e8f0;
    background: #ffffff;
  }

  .mobile-title {
    min-width: 0;
  }

  .mobile-title span {
    display: block;
    margin-top: 0.15rem;
    color: #64748b;
    font-size: 0.88rem;
    font-weight: 600;
  }

  .sheet-footer {
    border-top: 1px solid rgba(226, 232, 240, 0.9);
    border-bottom: 0;
  }

  h2 {
    margin: 0;
    color: #0f172a;
    font-size: 0.9rem;
    font-weight: 700;
  }

  .close-button,
  .ghost-button,
  .send-button,
  .tab-button {
    border-radius: 999px;
  }

  .close-button {
    display: grid;
    place-items: center;
    width: 2.4rem;
    height: 2.4rem;
    color: #475569;
    background: transparent;
  }

  .mobile-close {
    width: 2.85rem;
    height: 2.85rem;
    flex: 0 0 auto;
    color: #0f172a;
    background: transparent;
  }

  .toolbar {
    position: relative;
    display: grid;
    gap: 0.62rem;
    padding: 0.72rem 0.85rem 0;
  }

  .search-box {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    height: 2.25rem;
    padding: 0 0.75rem;
    border: 1px solid #93c5fd;
    border-radius: 0.18rem;
    color: #64748b;
    background: #ffffff;
  }

  .search-box input {
    width: 100%;
    color: #0f172a;
    background: transparent;
    outline: none;
    font-size: 0.78rem;
  }

  .tabs {
    display: flex;
    gap: 0.7rem;
    flex-wrap: wrap;
    padding-right: 7.5rem;
  }

  .filter-button {
    position: absolute;
    right: 1rem;
    bottom: -0.1rem;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    min-height: 2rem;
    padding: 0 0.2rem 0 0.7rem;
    color: #334155;
    font-size: 0.74rem;
    font-weight: 700;
  }

  .tab-button {
    padding: 0 0 0.48rem;
    border-radius: 0;
    color: #334155;
    background: transparent;
    font-size: 0.73rem;
    font-weight: 600;
  }

  .tab-button.active {
    color: #0f6fff;
    background: transparent;
    box-shadow: inset 0 -2px 0 #0f6fff;
  }

  .sheet-body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(12.25rem, 0.56fr);
    gap: 0;
    min-height: 0;
    min-width: 0;
    flex: 0 1 24.75rem;
    padding: 0.45rem 0.85rem 0;
    overflow: hidden;
  }

  .recipient-panel,
  .summary-panel {
    min-height: 0;
    min-width: 0;
    overflow: hidden;
    border: 0;
    border-radius: 0;
    background: #ffffff;
  }

  .recipient-panel {
    display: flex;
    flex-direction: column;
    min-width: 0;
    position: relative;
  }

  .panel-title,
  .selection-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.72rem 0.75rem;
    border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  }

  .panel-title strong,
  .selection-header strong {
    color: #0f172a;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .panel-title span,
  .selection-empty {
    color: #64748b;
    font-size: 0.72rem;
  }

  .recipient-list,
  .summary-panel {
    overflow: auto;
  }

  .recipient-list {
    display: grid;
    gap: 0.42rem;
    min-height: 0;
    min-width: 0;
    padding: 0.35rem 0.55rem 0.45rem 0;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    -webkit-overflow-scrolling: touch;
  }

  .summary-panel {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
    border-left: 1px solid rgba(226, 232, 240, 0.95);
    padding-bottom: 0.3rem;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    -webkit-overflow-scrolling: touch;
  }

  .summary-panel > :global(*) {
    min-width: 0;
  }

  .selection-header button {
    color: #0284c7;
    font-size: 0.72rem;
    font-weight: 600;
  }

  .selection-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    padding: 0.25rem 0.75rem;
    min-height: 0;
  }

  .selection-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.55rem 0.35rem 0.35rem;
    border-radius: 999px;
    color: #0f172a;
    background: transparent;
    font-size: 0.73rem;
    font-weight: 600;
    max-width: 100%;
  }

  .selection-chip-avatar {
    display: grid;
    place-items: center;
    width: 1.85rem;
    height: 1.85rem;
    flex: 0 0 auto;
    overflow: hidden;
    border-radius: 999px;
    color: #0f172a;
    background: #e2e8f0;
    font-size: 0.66rem;
    font-weight: 800;
  }

  .selection-chip-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .selection-chip span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .selection-empty {
    padding: 0 1rem;
  }

  .alphabet-rail {
    display: none;
  }

  .selected-avatar-strip {
    display: flex;
    align-items: center;
    gap: 1.05rem;
    min-height: 5rem;
    padding: 0.8rem 1rem 0.7rem;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }

  .selected-avatar-strip.empty {
    justify-content: center;
  }

  .selected-avatar-empty {
    color: #94a3b8;
    font-size: 0.9rem;
  }

  .selected-avatar {
    position: relative;
    display: grid;
    place-items: center;
    width: 3.25rem;
    height: 3.25rem;
    flex: 0 0 auto;
    overflow: visible;
    border-radius: 999px;
    color: #0f172a;
    background: #e2e8f0;
    font-weight: 800;
  }

  .selected-avatar img {
    width: 100%;
    height: 100%;
    border-radius: inherit;
    object-fit: cover;
  }

  .selected-avatar i {
    position: absolute;
    top: -0.35rem;
    right: -0.35rem;
    display: grid;
    place-items: center;
    width: 1.18rem;
    height: 1.18rem;
    border: 1px solid rgba(203, 213, 225, 0.94);
    border-radius: 999px;
    color: #64748b;
    background: #eef2f7;
    font-style: normal;
  }

  :global(.share-sheet .recipient-list::-webkit-scrollbar),
  :global(.share-sheet .summary-panel::-webkit-scrollbar) {
    width: 10px;
  }

  :global(.share-sheet .recipient-list::-webkit-scrollbar-thumb),
  :global(.share-sheet .summary-panel::-webkit-scrollbar-thumb) {
    border: 3px solid transparent;
    border-radius: 999px;
    background-clip: content-box;
    background-color: rgba(148, 163, 184, 0.5);
  }

  .note-box {
    display: grid;
    gap: 0.45rem;
    padding: 0;
  }

  .note-box span {
    color: #475569;
    font-size: 0.73rem;
    font-weight: 700;
  }

  .note-box textarea {
    width: 100%;
    padding: 0.68rem 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.18rem;
    color: #0f172a;
    background: rgba(255, 255, 255, 0.96);
    resize: none;
    outline: none;
    font-size: 0.78rem;
    line-height: 1.35;
  }

  .empty-results {
    padding: 2.6rem 1rem;
    color: #64748b;
    text-align: center;
    font-size: 0.8rem;
  }

  .ghost-button,
  .send-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 2.25rem;
    padding: 0.55rem 0.9rem;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .ghost-button {
    color: #475569;
    background: rgba(226, 232, 240, 0.74);
  }

  .send-button {
    color: white;
    background: #0f6fff;
    box-shadow: 0 12px 24px rgba(15, 111, 255, 0.22);
  }

  .desktop-compose-panel {
    display: grid;
    gap: 0.6rem;
    padding: 0.45rem 0.85rem 0.85rem;
    border-top: 1px solid #e2e8f0;
    background: #ffffff;
  }

  .send-button:disabled {
    opacity: 0.45;
    box-shadow: none;
    cursor: not-allowed;
  }

  @media (max-width: 900px) {
    .share-sheet {
      width: 100%;
      height: 100%;
      max-height: 100%;
      border-radius: 1.35rem;
    }

    .sheet-body {
      grid-template-columns: 1fr;
    }

    .summary-panel {
      max-height: 18rem;
    }
  }

  @media (max-width: 640px) {
    .share-sheet {
      width: 100%;
      height: 100dvh;
      max-height: 100%;
      border: 0;
      border-radius: 0;
      background: #ffffff;
      box-shadow: none;
    }

    .share-sheet.mobile-layout {
      height: 100dvh;
      min-height: 100dvh;
      padding-top: env(safe-area-inset-top);
    }

    .sheet-header,
    .sheet-footer,
    .toolbar,
    .sheet-body {
      padding-left: 0.9rem;
      padding-right: 0.9rem;
    }

    .sheet-header {
      justify-content: flex-start;
      min-height: 4.85rem;
      padding-top: 0.4rem;
      padding-bottom: 0.65rem;
      border-bottom-color: rgba(226, 232, 240, 0.95);
      background: rgba(255, 255, 255, 0.96);
    }

    .sheet-header h2 {
      margin: 0;
      font-size: 1.45rem;
      line-height: 1.15;
    }

    .toolbar {
      gap: 0.75rem;
      padding-top: 0.85rem;
      background: #fff;
    }

    .search-box {
      height: 2.85rem;
      border-radius: 0.9rem;
      background: #f1f5f9;
    }

    .sheet-body {
      display: flex;
      flex-direction: column;
      gap: 0.85rem;
      flex: 1 1 auto;
      min-height: 0;
      padding-top: 0.9rem;
      padding-bottom: 0;
      overflow: hidden;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }

    .recipient-panel,
    .summary-panel {
      border-radius: 1rem;
      background: #fff;
    }

    .recipient-panel {
      flex: 1 1 auto;
      min-height: 0;
      border: 0;
      padding-right: 1.1rem;
    }

    .recipient-list {
      flex: 1 1 auto;
      overflow: auto;
      padding: 0.45rem 0;
      gap: 0.25rem;
      scrollbar-gutter: auto;
    }

    .alphabet-rail {
      position: absolute;
      top: 3.1rem;
      right: -0.35rem;
      bottom: 0.2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      color: #7c8794;
      font-size: 0.78rem;
      font-weight: 700;
      line-height: 1;
      pointer-events: none;
    }

    .panel-title {
      padding: 0.35rem 0 0.55rem;
      border-bottom: 0;
    }

    .panel-title strong {
      font-size: 1rem;
    }

    .summary-panel {
      flex: 0 0 auto;
      max-height: min(15.5rem, 28dvh);
      margin: 0 -0.9rem;
      border-top: 1px solid rgba(226, 232, 240, 0.86);
      border-right: 0;
      border-bottom: 0;
      border-left: 0;
      border-radius: 1.15rem 1.15rem 0 0;
      overflow: auto;
      background: #fff;
      box-shadow: 0 -18px 36px rgba(15, 23, 42, 0.13);
      -webkit-overflow-scrolling: touch;
    }

    .selected-avatar-strip {
      min-height: 4.15rem;
      padding: 0.55rem 1rem 0.45rem;
      gap: 0.85rem;
    }

    .selected-avatar-strip {
      scrollbar-width: none;
    }

    .selected-avatar-strip::-webkit-scrollbar {
      display: none;
    }

    .note-box {
      padding: 0.35rem 1.05rem 0.05rem;
    }

    .note-box span {
      display: none;
    }

    .note-box textarea {
      min-height: 2.6rem;
      padding: 0.62rem 1rem;
      border: 0;
      border-radius: 1rem;
      background: #f5f5f5;
      font-size: 0.96rem;
    }

    .sheet-footer {
      position: sticky;
      bottom: 0;
      justify-content: flex-end;
      min-height: 4.45rem;
      padding-top: 0.2rem;
      padding-bottom: max(0.55rem, env(safe-area-inset-bottom));
      background: rgba(255, 255, 255, 0.96);
      box-shadow: none;
    }

    .ghost-button {
      display: none;
    }

    .send-button {
      display: grid;
      place-items: center;
      width: 3.65rem;
      height: 3.65rem;
      padding: 0;
      flex: 0 0 auto;
      border-radius: 999px;
      justify-content: center;
      background: #0877ff;
      box-shadow: none;
    }

    .send-button span {
      display: none;
    }
  }
</style>
